import React from 'react';
import { useTranslation } from 'react-i18next';
import { Url } from '@wix/da-url';
import { PapiUser, PapiPremiumFolderData } from '@wix/da-papi-types';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import EyeDisc2 from '@wix/da-ds/pkg/Icons/24x24/EyeDisc2';
import Premium from '@wix/da-ds/pkg/Icons/24x24/Premium';
import PointsIcon from '@wix/da-ds/pkg/Icons/Points';
import Button from '@wix/da-shared-react/pkg/Button';
import WatchButton from '@wix/da-shared-react/pkg/Button/ProfileOwnerWatchButton';
import BasePaywall from '../BasePaywall';
import { normalizePremiumFolderData } from '@wix/da-shared-react/pkg/utils/normalizePremiumFolderData';
import s from './FolderPaywall.scss';

export interface Props {
  premiumFolderData?: NonNullable<PapiPremiumFolderData>;
  contentOwner: PapiUser;
  folderId?: number;
  biData?: any;
  children: React.ReactNode;
}

const FolderPaywall: React.FC<Props> = ({
  contentOwner,
  premiumFolderData,
  biData,
  children,
}) => {
  const { t } = useTranslation();
  const { type, hasAccess, pointsPrice, galleryUrl, subproductId } =
    premiumFolderData || {};

  const { formattedDollarPrice } =
    normalizePremiumFolderData(premiumFolderData);

  return (
    <>
      {type === 'paid' && (
        <BasePaywall
          hasAccess={!!hasAccess}
          icon={<Premium size={IconSize.MEDIUM} />}
          title={t('paywall.premium.folder.title')}
          text={t('paywall.premium.folder.text')}
          button={
            <Button
              view="b18"
              fullWidth={false}
              href={subproductId ? Url.checkoutLink(subproductId) : undefined}
              biData={biData}
            >
              <span className={s['buttonLabel']}>
                {t('paywall.premium.folder.unlockFor')} {formattedDollarPrice}
                {pointsPrice !== undefined && pointsPrice < 80 && (
                  <>
                    {' / '}
                    <PointsIcon size={IconSize.TINY} disableDefaultColors />
                    {pointsPrice}
                  </>
                )}
              </span>
            </Button>
          }
        >
          {children}
        </BasePaywall>
      )}
      {type === 'watchers' && (
        <BasePaywall
          hasAccess={!!hasAccess}
          icon={<EyeDisc2 size={IconSize.MEDIUM} />}
          title={t('paywall.watchersOnly.folder.title')}
          text={t('paywall.watchersOnly.folder.text')}
          button={
            <WatchButton
              view="b1"
              user={contentOwner!}
              fullWidth={false}
              onToggleWatchSuccess={() => {
                window.location.href = galleryUrl!;
              }}
            />
          }
        >
          {children}
        </BasePaywall>
      )}
      {!type && children}
    </>
  );
};
FolderPaywall.displayName = 'FolderPaywall';

export default FolderPaywall;
